import { UserProfile as Profile, User } from 'oidc-client-ts'
import React, { createContext, useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { AppRoutesNavigation } from '../app-routes'
import { axios } from '../axios'
import { UserRole } from '../enums'
import { userManager } from '../user-manager'

interface ProfileExtension {
  role: UserRole
}

interface ContextProps {
  user: User
  userProfile: Profile & ProfileExtension
}

export const IdentityServerUserCtx = createContext<ContextProps>(null!)

export const IdentityServerUserContextProvider: React.FC = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    getUser()

    // Update axios header and user state when token is silently renewed via refresh token
    const onUserLoaded = (renewedUser: User) => {
      axios.defaults.headers['Authorization'] = `Bearer ${renewedUser.access_token}`
      setUser(renewedUser)
    }

    userManager.events.addUserLoaded(onUserLoaded)

    return () => {
      userManager.events.removeUserLoaded(onUserLoaded)
    }
  }, [])

  const getUser = async () => {
    await userManager.clearStaleState()

    // Only process OIDC callback when we're actually on the redirect URI (URL has state/code from IdP)
    const urlParams = new URLSearchParams(window.location.search)
    const isOidcCallback = urlParams.has('state') && (urlParams.has('code') || urlParams.has('id_token'))

    if (isOidcCallback) {
      try {
        const callbackUser = await userManager.signinCallback()
        if (callbackUser) {
          Logger.info('Got user after login')
          axios.defaults.headers['Authorization'] = `Bearer ${callbackUser.access_token}`
          setUser(callbackUser)
          setLoading(false)
          return
        }
      } catch (e) {
        Logger.info('Error processing sign-in callback', e)
      }
    }

    const user = await userManager.getUser()

    Logger.debug('Authorized user from storage: ', user)
    axios.defaults.headers['Authorization'] = `Bearer ${user?.access_token}`

    userManager.startSilentRenew()
    setUser(user)
    setLoading(false)
  }

  if (!user && loading) return null

  if (!user) return <Navigate to={AppRoutesNavigation.Login} />

  const contextValue: ContextProps = {
    user,
    userProfile: user.profile as any,
  }

  return <IdentityServerUserCtx.Provider value={contextValue}>{children}</IdentityServerUserCtx.Provider>
}